import React, { useState, useCallback } from 'react';

// @mui
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
// hooks
// utils
// assets
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

// components
import Iconify from 'src/components/iconify';

import { useTranslate } from '../../../../locales';
import { FormTab } from './components/property/form-tab';
import { AssetsTab } from './components/property/assets-tab';
import { useResponsive } from '../../../../hooks/use-responsive';
import { useSettingsContext } from '../../../../components/settings';
import { ComponentTypeProps } from '../../../../types/page-generator';

// ----------------------------------------------------------------------

type AccountTab = {
  value: string;
  label: string;
};

export default function Property({ block }: ComponentTypeProps) {
  const { t } = useTranslate();
  const isMobile = useResponsive('down', 'sm');
  const settings = useSettingsContext();
  const [activeAction, setActiveAction] = useState('');
  const [currentTab, setCurrentTab] = useState('assets');
  const quickActions = [
    'פטור מתשלום נכס ריק / שיפוצים',
    'בקשה מקוונת להוראת קבע',
    'אתר רשות עירוני',
    'הרשמה לניוזלטר',
    'חילוף מחזיקים בנכס',
    'זימון תור',
  ];
  const TABS: AccountTab[] = [
    {
      value: 'assets',
      label: 'Your assets',
    },
    {
      value: 'form',
      label: 'Forms and information',
    },
  ];

  const assetsTabItem = Object.prototype.hasOwnProperty.call(block, 'propertyAssetsTabItem')
    ? block?.propertyAssetsTabItem
    : true;
  const formTabItem = Object.prototype.hasOwnProperty.call(block, 'propertyFormTabItem')
    ? block?.propertyFormTabItem
    : true;

  const activeTabNames: string[] = [];

  if (assetsTabItem) activeTabNames.push('assets');
  if (formTabItem) activeTabNames.push('form');

  const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);

  return (
    <Card sx={{ borderRadius: 1, padding: 4 }}>
      <Stack width={1} gap={2}>
        <Stack direction="row" alignItems="center" gap={2}>
          <Box
            sx={{
              background: 'rgb(0, 89, 210)',
              borderRadius: 1,
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Iconify icon="tdesign:home" width={30} color="white" />
          </Box>
          <Typography sx={{ fontSize: '24px', fontWeight: 1000 }}>{`${t('Property')}`}</Typography>
        </Stack>
        <Stack
          sx={{
            borderRadius: '5px',
            border: '1px solid',
            borderColor: '#d9d9d9',
            padding: '16px',
          }}
        >
          <Typography sx={{ fontSize: '18px', fontWeight: 1000 }}>פעולות מהירות</Typography>
          <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
            {quickActions.map((item: string, id) => (
              <Stack
                key={id}
                direction="row"
                gap={1}
                alignItems="center"
                sx={{
                  borderLeft: activeAction === item && id !== 0 ? '1px solid blue' : 'none',
                  borderRight:
                    activeAction === item && id !== quickActions.length - 1
                      ? '1px solid blue'
                      : 'none',
                }}
              >
                <Iconify icon="iconoir:page" width={10} color="#282894" />
                <Typography
                  onClick={() => setActiveAction(item)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { color: 'blue' },
                  }}
                >
                  {item}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
        <Stack
          direction={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          alignItems="center"
          sx={{
            border: '1px solid #0059d2',
            borderRadius: '5px',
            padding: '5px',
          }}
        >
          <Stack direction={isMobile ? 'column' : 'row'} gap={1} alignItems="center">
            <Iconify
              icon="ic:outline-mail"
              color="#000080"
              sx={{ width: '30px', height: '30px' }}
            />
            <Typography sx={{ fontWeight: 600 }}>קיימת יתרה שלא שולמה באחד מהנכסים שלך-</Typography>
            <Typography>בואו נסגור את היתרה</Typography>
          </Stack>
          <Button
            variant="contained"
            sx={{
              color: 'white',
              bgcolor: '#0059d2',
              borderRadius: '25px',
              px: '40px',
            }}
          >
            מעבר לתשלום
          </Button>
        </Stack>
        <Tabs value={currentTab} onChange={handleChangeTab}>
          {TABS.filter((tab: AccountTab) => activeTabNames.includes(tab.value)).map(
            (tab: AccountTab) => (
              <Tab key={tab.value} label={`${t(`${tab.label}`)}`} value={tab.value} />
            )
          )}
        </Tabs>

        {currentTab === 'assets' && assetsTabItem && <AssetsTab block={block} />}

        {currentTab === 'form' && formTabItem && <FormTab block={block} />}
      </Stack>
    </Card>
  );
}
